import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import {
  type Airport,
  type FiatPrice,
  MultiTicketType,
  type Person,
  SeatMapResponseEnum,
  type SeatSlice,
} from "@b2bportal/air-booking-api";
import type { TripSlice } from "@b2bportal/air-shopping-api";
import { type FailedProduct, Product } from "@b2bportal/purchase-api";

import {
  AirChfarExerciseDiscountSelectors,
  AirDisruptionSelectors,
  AirPFExerciseSelectors,
  cartQuoteSelectors,
  ContactSelectors,
  FlightPassengerEventTypes,
  FlightPassengerSelectors,
  FlightSelectors,
  PassengerSelectors,
  PriceBreakdownContent,
  type PriceChangeType,
  SeatSelectors,
  useCheckoutSend,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { I18nMarkup, useI18nContext } from "@hopper-b2b/i18n";
import { getDarkModePreferred } from "@hopper-b2b/themes";
import {
  type DisruptionProductType,
  type FlightSummaryInfo,
  type GordianSeatSegment,
  type IPerson,
  type SelectedSeatsSegment,
  TripCategory,
  type TripDetails,
} from "@hopper-b2b/types";
import {
  ActionLink,
  ButtonWrap,
  ContactSummaryRow,
  ErrorPopup,
  IconComponent,
  IconName,
  MobileFlightSummaryRowNew,
  MobilePopoverCard,
  PassengerSummaryRow,
  SeatsSummaryRow,
  Slot,
  UberPriceSummaryRow,
} from "@hopper-b2b/ui";
import {
  getSelectedSeatsSegments,
  getSliceIndex,
  useDeviceTypes,
  useEnableDarkMode,
  useEnableVipSupport,
  useHideAirPriceFreezeHackerFare,
  useUberBridge,
} from "@hopper-b2b/utilities";
import { Divider } from "@material-ui/core";
import clsx from "clsx";
import AirplaneArrive from "../../../../assets/client/airplane-arrive.svg";
import AirplaneDepart from "../../../../assets/client/airplane-depart.svg";
import Card from "../../../../assets/client/card.svg";
import AirplaneArriveWhite from "../../../../assets/client/darkMode/airplane-arrive-white.svg";
import AirplaneDepartWhite from "../../../../assets/client/darkMode/airplane-depart-white.svg";
import CardWhite from "../../../../assets/client/darkMode/card-white.svg";
import WhitePassengerIcon from "../../../../assets/client/darkMode/passenger-white.svg";
import WhitePlusIcon from "../../../../assets/client/darkMode/plus_small-white.svg";
import PassengerIcon from "../../../../assets/client/passenger.svg";
import PlusIcon from "../../../../assets/client/plus_small.svg";
import {
  FtuCashbackText,
  FtuFlightReviewBanner,
} from "../../../../components/FtuExperiment";
import { useFtuExperiment } from "../../../../utils/ftuExperiments";
import { PATH_FLIGHTS_SEARCH } from "../../../../utils/urlPaths";
import { Event, type TEvent } from "../../events";
import {
  getFlightTotalInPrices,
  getIsPriceBreakdownLoading,
  getIsRyanair,
  getPriceFreezePerPaxDiscountAmount,
  getShopPricingInfo,
  getSummaryLineItems,
  getTripPriceLineItems,
} from "../../selectors";
import { ConfirmRyanairPopover } from "./components";
import { FareDetailsModal } from "./components/FareDetailsModal";
import { ItineraryDetailsModal } from "./components/ItineraryDetailsModal";
import { PriceFreezeSummaryRow } from "./components/PriceFreezeSummaryRow";
import { ReviewLayout } from "./components/ReviewLayout";
import { VipSupportPanel } from "./components/VipSupportPanel";
import { SelfTransferDisclaimer } from "@/components/Slots";
import "./styles.scss";

export const ConnectedReviewComponent = () => {
  const [openPriceBreakdownModal, setOpenPriceBreakdownModal] =
    useState<boolean>(false);
  const { t } = useI18nContext();
  const { enabled: ftuEnabled } = useFtuExperiment();
  const { setHeader } = useUberBridge();
  const history = useHistory();
  const contactInformation = useSelector(
    ContactSelectors.getContactInformation
  );
  const send = useCheckoutSend<TEvent>();
  const darkModeAllowed = useEnableDarkMode();
  const isDarkMode = darkModeAllowed && getDarkModePreferred();

  const showVipSupport = useEnableVipSupport();
  const selectedPassengersWithType = useSelector(
    FlightSelectors.mergePassengersAndBreakdownPersons
  );
  const passengers = useSelector(
    FlightPassengerSelectors.getUserPassengersParent
  );

  const seatMapAvailable =
    useSelector(SeatSelectors.getSeatMapAvailability) ===
    SeatMapResponseEnum.SeatMapAvailable;

  const selectedSeatSegments = useSelector(
    SeatSelectors.getSelectedSeatSegments
  );
  const seatSlices = useSelector(SeatSelectors.getSeatSlices);
  const seatedPassengers = useSelector(
    FlightPassengerSelectors.getSeatedPassengersParent
  );
  const isCartLocked = useSelector(cartQuoteSelectors.getCartQuoteIsLocked);
  const priceChange = useSelector(cartQuoteSelectors.getCartQuotePriceChange);
  const unavailableProducts = useSelector(
    cartQuoteSelectors.getUnavailableProducts
  );
  const disruptionOffersProducts = useSelector(
    AirDisruptionSelectors.getDisruptionOfferProductTypes
  );
  const flightTotalInPrices = useSelector(getFlightTotalInPrices);
  const tripPriceLineItems = useSelector(getTripPriceLineItems);
  const tripPricingSummaryItems = useSelector(getSummaryLineItems);
  const airports = useSelector(FlightSelectors.getAirportMap);
  // Flight Shop Summary Panel
  const tripDetails = useSelector(FlightSelectors.getSelectedTripDetailsParent);
  const tripCategory = useSelector(FlightSelectors.getTripCategoryParent);
  const isPriceBreakdownLoading = useSelector(getIsPriceBreakdownLoading);
  const shopPricingInfo = useSelector(getShopPricingInfo);
  const flightContainsRyanair = useSelector(getIsRyanair);
  //
  const initialPassengerPricing = useMemo(
    () => shopPricingInfo?.fare?.[0]?.pricing,
    [shopPricingInfo?.fare]
  );

  const priceFreezeSavingPrice = useSelector(
    getPriceFreezePerPaxDiscountAmount
  )?.fiat;

  const [showRyanairConfirm, setShowRyanairConfirm] = useState(false);

  const onBookFlight = useCallback(() => {
    if (flightContainsRyanair) {
      setShowRyanairConfirm(true);
      return;
    }

    send(Event.NEXT);
  }, [send, flightContainsRyanair]);

  const addSeats = useCallback(() => send(Event.GO_TO_SEAT_SELECTION), [send]);

  // If Cart is locked, then seats have already been added, have to rerun PQ
  const handleEditSeatSelection = useCallback(() => {
    if (isCartLocked) {
      send(Event.GO_TO_CART_QUOTE);
    } else {
      send(Event.EDIT_SEATS_FROM_REVIEW);
    }
  }, [isCartLocked, send]);

  useEffect(() => {
    setHeader({
      title: t("reviewDetails"),
    });
  }, [setHeader, t]);

  const tripCurrency = useMemo(
    () => ({
      currencyCode: flightTotalInPrices?.fiat?.currencyCode,
      currencySymbol: flightTotalInPrices?.fiat?.currencySymbol,
    }),
    [
      flightTotalInPrices?.fiat?.currencyCode,
      flightTotalInPrices?.fiat?.currencySymbol,
    ]
  );
  const tripTotalCurrency = useMemo(
    () =>
      t("chargedInCurrency", {
        currency: tripCurrency?.currencyCode,
      }),
    [tripCurrency, t]
  );

  const tripPriceLineItemsWithType = useMemo(() => {
    return tripPriceLineItems.map((item) => {
      const passenger = selectedPassengersWithType?.find(
        (passenger) => passenger.id === item.id
      );
      const type = passenger?.passengerType;
      const typeTranslation =
        type === "C08"
          ? t("passengerInformation.child")
          : type === "ADT"
            ? t("passengerInformation.adult")
            : t("passengerInformation.infant");
      item.lineTitle = `${item.lineTitle} (${typeTranslation})`;
      return item;
    });
  }, [selectedPassengersWithType, t, tripPriceLineItems]);

  const totalPrice: FiatPrice = useMemo(() => {
    return {
      ...(flightTotalInPrices?.fiat || {
        currencyCode: "GBP",
        currencySymbol: "£",
      }),
      value: flightTotalInPrices?.fiat?.value
        ? flightTotalInPrices.fiat.value
        : 0,
    };
  }, [flightTotalInPrices.fiat]);

  return (
    <>
      {priceChange?.hasDifference ? (
        <PriceChangeModal
          {...priceChange}
          currencyCode={tripCurrency.currencyCode}
        />
      ) : null}
      {unavailableProducts.length > 0 ? (
        <UnavailableProductsModal
          unavailableProducts={unavailableProducts}
          disruptionOffersProducts={disruptionOffersProducts}
        />
      ) : null}
      <ReviewLayout
        handleGoBack={() => history.goBack()}
        onBookFlight={onBookFlight}
        header={false}
        headerTitle={t("reviewDetails")}
        ctaLabel={t("continueToPayment")}
      >
        <FtuFlightReviewBanner />
        <FlightShopSummaryPanel
          tripDetails={tripDetails}
          airports={airports}
          ftuEnabled={ftuEnabled}
        />
        <ContactSummaryRow
          contactInfo={contactInformation}
          onEdit={() => send(Event.GO_TO_CONTACT_INFORMATION)}
          hideIcon
        />
        <PassengerSummaryPanel
          passengers={passengers}
          selectedPassengers={selectedPassengersWithType}
        />
        {showVipSupport ? (
          <VipSupportPanel
            // TODO: add a request to get the price for vip
            vipSupportPrice={
              {
                currencyCode: "GBP",
                currencySymbol: "£",
                value: 9,
              } as FiatPrice
            }
          />
        ) : undefined}
        {seatMapAvailable || selectedSeatSegments.length > 0 ? (
          <SeatsSummaryPanel
            selectedSeatSegments={selectedSeatSegments}
            tripDetails={tripDetails}
            seatedPassengers={seatedPassengers}
            onEditSeatSelectionClick={handleEditSeatSelection}
            airports={airports}
            addSeats={addSeats}
            seatSlices={seatSlices}
          />
        ) : null}
        <PricingSummaryPanel
          price={totalPrice}
          tripCategory={tripCategory}
          onClick={() => setOpenPriceBreakdownModal(true)}
          priceFreezeSavingPrice={priceFreezeSavingPrice}
        />
      </ReviewLayout>
      <MobilePopoverCard
        centered
        fullScreen
        open={openPriceBreakdownModal}
        className="review-price-breakdown-modal"
        contentClassName={clsx("review-price-breakdown-modal-content", {
          darkMode: isDarkMode,
        })}
        onClose={() => setOpenPriceBreakdownModal(false)}
        topRightButton={
          <ActionLink
            className="review-price-breakdown-modal-close-button"
            content={
              <IconComponent
                ariaLabel={t("close")}
                className={clsx("close-button-icon")}
                name={IconName.Close}
              />
            }
            label={t("close")}
            onClick={() => setOpenPriceBreakdownModal(false)}
          />
        }
        headerElement={
          <div className="review-price-breakdown-modal-header">
            {t("priceBreakdown")}
          </div>
        }
      >
        <PriceBreakdownContent
          isLoading={isPriceBreakdownLoading}
          currency={tripCurrency}
          lineItems={tripPriceLineItemsWithType}
          summaryLineItems={tripPricingSummaryItems}
          header={t("checkoutBreakdown")}
          initialPassengerPricing={initialPassengerPricing}
          tripCategory={tripCategory}
          currencyString={tripTotalCurrency}
          priceItemIcon={
            <img
              className="pricing-breakdown-passenger-icon"
              src={isDarkMode ? WhitePassengerIcon : PassengerIcon}
              alt=""
            />
          }
        />
      </MobilePopoverCard>
      <ConfirmRyanairPopover
        isOpen={showRyanairConfirm}
        onCancel={() => setShowRyanairConfirm(false)}
        onContinue={() => send(Event.NEXT)}
      />
    </>
  );
};

interface UnavailableProductsModalProps {
  unavailableProducts: Array<FailedProduct>;
  disruptionOffersProducts: Array<DisruptionProductType>;
}

const UnavailableProductsModal = ({
  unavailableProducts,
  disruptionOffersProducts,
}: UnavailableProductsModalProps) => {
  const { t } = useI18nContext();
  const history = useHistory();

  const toFlightSelection = () => {
    history.push(PATH_FLIGHTS_SEARCH);
  };

  const [isOpen, setIsOpen] = useState(true);

  const getSubtitle = useCallback(() => {
    const mappedProductToI18nKey = {
      [Product.AirCfar]: "AirCfar",
      [Product.AirPriceFreeze]: "priceFreezePurchase.documentTitle",
      // TODO: Seats will not be added until cart can be mutable with seats
      [Product.Seats]: "Seats",
    };
    const products = unavailableProducts.reduce(
      (listOfProducts, product) => [
        ...(product.id.product === Product.AirDisruption
          ? disruptionOffersProducts.map((disruptionProducts) =>
              t(disruptionProducts)
            )
          : [t(mappedProductToI18nKey[product.id.product])]),
        ...listOfProducts,
      ],
      [] as Array<string>
    );

    return products.length > 1
      ? t("unavailableProductsMultiple", { products })
      : t("unavailableProductSingle", { products });
  }, [unavailableProducts, disruptionOffersProducts, t]);

  return (
    <ErrorPopup
      open={isOpen}
      title={t("genericShopErrorTitle")}
      subtitle={getSubtitle()}
      primaryButtonText={t("continue")}
      primaryOnClick={() => {
        setIsOpen(false);
      }}
      secondaryButtonText={t("flightShopReview.changeFlight")}
      secondaryOnClick={toFlightSelection}
    />
  );
};

interface PriceChangeModalProps extends PriceChangeType {
  currencyCode: string;
}

const PriceChangeModal = (props: PriceChangeModalProps) => {
  const { t, formatFiatCurrency } = useI18nContext();
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(true);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toFlightSelection = useCallback(() => {
    history.push(PATH_FLIGHTS_SEARCH);
  }, [history]);

  return props.isIncrease ? (
    <ErrorPopup
      open={isOpen}
      centered={false}
      title={t("priceChange.priceIncreaseTitle", {
        amount: formatFiatCurrency({
          value: props.priceQuoteTotal,
          currencyCode: props.currencyCode,
        }),
      })}
      subtitle={t("priceChange.priceIncreaseSubtitle")}
      primaryButtonText={t("continue")}
      primaryOnClick={onClose}
      secondaryButtonText={t("flightShopReview.changeFlight")}
      secondaryOnClick={toFlightSelection}
    />
  ) : (
    <ErrorPopup
      open={isOpen}
      centered={false}
      title={t("priceChange.priceDecreaseTitle", {
        amount: formatFiatCurrency({
          value: props.priceQuoteTotal,
          currencyCode: props.currencyCode,
        }),
      })}
      subtitle={t("priceChange.priceDecreaseSubtitle")}
      primaryButtonText={t("continue")}
      primaryOnClick={onClose}
    />
  );
};

interface FlightShopSummaryPanelProps {
  tripDetails: TripDetails;
  airports: { [key: string]: Airport } | undefined;
  ftuEnabled: boolean;
}

const FlightShopSummaryPanel = (props: FlightShopSummaryPanelProps) => {
  const { t } = useI18nContext();
  const [openFareModal, setOpenFareModal] = useState<boolean>(false);
  const darkModeAllowed = useEnableDarkMode();
  const isDarkMode = darkModeAllowed && getDarkModePreferred();
  const { tripDetails, airports, ftuEnabled } = props;
  const departureSliceIndex = getSliceIndex(true, tripDetails);
  const departureSlice = tripDetails?.slices[departureSliceIndex];

  const returnSliceIndex = getSliceIndex(false, tripDetails);
  const hasReturnFlight = returnSliceIndex !== -1;

  const [openItineraryDetails, setOpenItineraryDetails] = useState<
    "departure" | "return" | null
  >(null);

  const returnSlice = hasReturnFlight
    ? tripDetails.slices[returnSliceIndex]
    : null;

  const isVirtualInterlining =
    tripDetails?.fareDetails[0]?.multiTicketType ===
    MultiTicketType.virtualInterline;

  const getFlightSummaryInfo = (tripSlice: TripSlice): FlightSummaryInfo => {
    const flightSegment = tripSlice.segmentDetails[0];

    const originLabel = airports
      ? airports[tripSlice.originCode]?.cityName
      : tripSlice.originName;
    const destinationLabel = airports
      ? airports[tripSlice.destinationCode]?.cityName
      : tripSlice.destinationName;
    const uniqueAirlineCodes = new Set(
      tripSlice.segmentDetails.map((segment) => segment.airlineCode)
    );
    const numberOfUniqueAirlines = isVirtualInterlining
      ? uniqueAirlineCodes.size
      : 1;

    return {
      originCity: tripSlice.outgoing ? originLabel : destinationLabel,
      destinationCity: tripSlice.outgoing ? destinationLabel : originLabel,
      departure: tripSlice.departureTime,
      arrival: tripSlice.arrivalTime,
      airlineCode: flightSegment.airlineCode,
      airlineName: flightSegment.airlineName,
      isDeparture: tripSlice.outgoing,
      title: t("originToDestination", {
        origin: originLabel,
        destination: destinationLabel,
      }),
      stops: tripSlice.stops,
      numberOfUniqueAirlines,
    };
  };

  return (
    <>
      <div
        className={clsx(
          "mobile-itinerary-cards-section",
          "review-flight-details-section"
        )}
      >
        <h3
          className={clsx("flight-summary-section-header", {
            hidden: ftuEnabled,
          })}
        >
          {t("flights")}
        </h3>
        <MobileFlightSummaryRowNew
          flightSummaryInfo={getFlightSummaryInfo(departureSlice)}
          iconSrc={isDarkMode ? AirplaneDepartWhite : AirplaneDepart}
          onClick={() => setOpenItineraryDetails("departure")}
        />
        {hasReturnFlight && returnSlice ? (
          <>
            <Divider className="mobile-review-return-flight-divider" />
            <MobileFlightSummaryRowNew
              flightSummaryInfo={getFlightSummaryInfo(returnSlice)}
              iconSrc={isDarkMode ? AirplaneArriveWhite : AirplaneArrive}
              className="return-flight-details"
              onClick={() => setOpenItineraryDetails("return")}
            />
          </>
        ) : null}
        {isVirtualInterlining ? <SelfTransferDisclaimer /> : null}
        <div className="review-fare-details-container">
          <p className="fare-details-title">{t("reviewFareDetailsTitle")}</p>
          <ButtonWrap
            className="fare-details-link"
            onClick={() => setOpenFareModal(true)}
          >
            {t("reviewFareDetailsButton")}
          </ButtonWrap>
        </div>
        <Divider className="mobile-review-flight-book-divider" />
      </div>
      <FareDetailsModal
        open={openFareModal}
        onClose={() => setOpenFareModal(false)}
      />
      <ItineraryDetailsModal
        openModal={openItineraryDetails}
        onClose={() => setOpenItineraryDetails(null)}
      />
    </>
  );
};

interface PassengerSummaryPanelProps {
  passengers: Person[];
  selectedPassengers: Person[];
}

const PassengerSummaryPanel = (props: PassengerSummaryPanelProps) => {
  const { passengers, selectedPassengers } = props;
  const { t } = useI18nContext();
  const send = useCheckoutSend<TEvent>();
  const darkModeAllowed = useEnableDarkMode();
  const isDarkMode = darkModeAllowed && getDarkModePreferred();

  const onEdit = useCallback(
    (passenger: Person) => {
      send({
        type: FlightPassengerEventTypes.OPEN_FORM_AND_SET_PASSENGER,
        passenger,
      });
    },
    [send]
  );

  const goToPassengerPicker = useCallback(() => {
    if (passengers.length === selectedPassengers.length) {
      send({
        type: FlightPassengerEventTypes.OPEN_FORM_AND_SET_PASSENGER,
        passenger: {} as Person,
      });
    } else {
      send({
        type: FlightPassengerEventTypes.OPEN_PASSENGER_PICKER,
      });
    }
  }, [passengers.length, selectedPassengers.length, send]);

  return (
    <div
      className={clsx(
        "mobile-itinerary-cards-section",
        "review-passenger-section"
      )}
    >
      <div className="mobile-trip-details-passenger-info-container">
        <PassengerSummaryRow
          passengers={selectedPassengers as IPerson[]}
          onEdit={onEdit}
          review
          hideIcon
        />
        <button className="add-passenger-button" onClick={goToPassengerPicker}>
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <img src={isDarkMode ? WhitePlusIcon : PlusIcon} />
          {t("addNewPassenger")}
        </button>
      </div>

      <Divider className="mobile-review-flight-book-divider" />
    </div>
  );
};

interface PricingSummaryPanelProps {
  price: FiatPrice;
  tripCategory: TripCategory;
  priceFreezeSavingPrice?: FiatPrice;
  onClick: () => void;
}

const PricingSummaryPanel = (props: PricingSummaryPanelProps) => {
  const { t, formatFiatCurrency } = useI18nContext();
  const darkModeAllowed = useEnableDarkMode();
  const isDarkMode = darkModeAllowed && getDarkModePreferred();
  const { price, tripCategory, onClick, priceFreezeSavingPrice } = props;
  const priceFreezeOffer = useSelector(
    AirPFExerciseSelectors.getCheapestPriceFreezeOfferData
  );
  const passengers = useSelector(PassengerSelectors.getSelectedPassengerIds);
  const paxCount = passengers?.length || 0;

  const isChfarExercise = useSelector(
    AirChfarExerciseDiscountSelectors.getChfarId
  );

  const flightMultiTicketType = useSelector(
    FlightSelectors.getFlightMultiTicketType
  );
  const hidePriceFreezeHackerFare = useHideAirPriceFreezeHackerFare();

  const description = useMemo(() => {
    const priceLabelCopy =
      tripCategory === TripCategory.ROUND_TRIP
        ? t("roundTripCategoryTotal")
        : t("oneWayTripCategoryTotal");

    return (
      <>
        <p className="price-review-label">{priceLabelCopy}</p>
        <FtuCashbackText price={price} paxCount={paxCount} />
      </>
    );
  }, [paxCount, price, t, tripCategory]);

  const priceFreezeSavingText = priceFreezeSavingPrice
    ? t("priceFreezeExerciseSavingText", {
        amount: formatFiatCurrency(priceFreezeSavingPrice),
      })
    : "";

  return (
    <div
      className={clsx(
        "mobile-itinerary-cards-section",
        "review-price-section",
        { "price-freeze": !!priceFreezeOffer }
      )}
    >
      <UberPriceSummaryRow
        priceString={formatFiatCurrency(price, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}
        iconSrc={isDarkMode ? CardWhite : Card}
        description={description}
        onClick={onClick}
        priceFreezeText={priceFreezeSavingText}
        cashBackElement={
          <Slot
            id="cash-back-ftu-discount"
            price={price}
            passengers={passengers as IPerson[]}
          />
        }
      />
      {isChfarExercise ? null : flightMultiTicketType ===
        MultiTicketType.hackerFare ? (
        hidePriceFreezeHackerFare ? null : (
          <PriceFreezeSummaryRow />
        )
      ) : (
        <PriceFreezeSummaryRow />
      )}
    </div>
  );
};

interface SeatsSummaryPanelProps {
  selectedSeatSegments: GordianSeatSegment[];
  tripDetails: TripDetails;
  seatedPassengers: Person[];
  onEditSeatSelectionClick: (idx: number) => void;
  addSeats: () => void;
  airports: { [key: string]: Airport };
  seatSlices: Array<SeatSlice>;
}

const SeatsSummaryPanel = (props: SeatsSummaryPanelProps) => {
  const {
    selectedSeatSegments,
    tripDetails,
    seatedPassengers,
    onEditSeatSelectionClick,
    addSeats,
    airports,
    seatSlices,
  } = props;
  const { formatCurrency } = useI18nContext();
  const { matchesMobile } = useDeviceTypes();

  const [outboundSegmentsSeats, setOutboundSegmentsSeats] = useState<
    SelectedSeatsSegment[]
  >([]);
  const [returnSegmentsSeats, setReturnSegmentsSeats] = useState<
    SelectedSeatsSegment[]
  >([]);

  const totalSeatPricing = useSelector(SeatSelectors.getSeatTotalPricing);
  const selectedSeats = useSelector(SeatSelectors.getSelectedSeats);

  useEffect(() => {
    if (selectedSeatSegments.length > 0) {
      const outboundSegmentSelectedSeats = getSelectedSeatsSegments(
        selectedSeatSegments,
        tripDetails.slices[0],
        seatedPassengers,
        tripDetails.slices[0].segmentDetails.length,
        seatSlices[0]
      );
      setOutboundSegmentsSeats(outboundSegmentSelectedSeats);
      if (tripDetails.slices.length > 1) {
        const returnSegmentSelectedSeats = getSelectedSeatsSegments(
          selectedSeatSegments,
          tripDetails.slices[1],
          seatedPassengers,
          tripDetails.slices[0].segmentDetails.length,
          seatSlices[1]
        );
        setReturnSegmentsSeats(returnSegmentSelectedSeats);
      }
    }
  }, [seatSlices, seatedPassengers, selectedSeatSegments, tripDetails.slices]);

  const description = useMemo(() => {
    return (
      <div className="seats-summary-row-description">
        <I18nMarkup
          tKey={"seats.totalSeatPricing"}
          replacements={{
            count: selectedSeats.length,
            price: formatCurrency(totalSeatPricing),
          }}
        />
      </div>
    );
  }, [formatCurrency, selectedSeats.length, totalSeatPricing]);

  return (
    <div
      className={clsx(
        "mobile-itinerary-cards-section",
        "seat-selection-review-container",
        "uber",
        "review",
        {
          mobile: matchesMobile,
        }
      )}
    >
      <SeatsSummaryRow
        outboundSeatSegments={outboundSegmentsSeats}
        returnSeatSegments={returnSegmentsSeats}
        onEditClick={onEditSeatSelectionClick}
        outboundOriginCode={tripDetails.slices[0].originCode}
        returnOriginCode={
          tripDetails.slices.length > 1
            ? tripDetails.slices[1].originCode
            : undefined
        }
        airports={airports}
        description={description}
        addSeats={addSeats}
      />
      <Divider className="mobile-review-flight-book-divider" />
    </div>
  );
};
